/**
 * "Divide into sections" pure logic (no React, no DOM): gathering the script
 * for analysis, coloring the answer, laying out the preview highlight bands,
 * and wording the errors. Everything the panel and the overlay compute lives
 * here so it is testable without a board.
 */
import type { BoardDoc } from "@kola/shared";

import type {
  ApiError,
  SectionsSuggestResponse,
  SectionsSuggestRow,
  SuggestedSection,
} from "../../core/api.js";
import { BG_PRESETS } from "../selection-toolbar/palette.js";
import { upstreamMessage } from "../script/brainstorm.js";
import type { MoodPreview, SectionPreview } from "./store.js";

/**
 * Client-side mirrors of the server's configured caps (core/config.ts
 * DEFAULT_SECTIONS_*), so an over-length script is refused with a real
 * explanation instead of a bare 400. Same convention as brainstorm's
 * pre-cutting; a server configured LOWER than these still answers with
 * too-many-rows/chars, which sectionsErrorMessage words the same way.
 */
export const SECTIONS_MAX_ROWS = 1_000;
export const SECTIONS_MAX_CHARS_TOTAL = 60_000;
/** Per-row cap — the route schema's maxLength; longer rows are TRIMMED for
 * the request only (topic grouping survives a truncated line; a 400 doesn't). */
export const SECTIONS_ROW_MAX_CHARS = 4_000;

/**
 * Section highlight palette: the F-17 pastel presets minus gray — gray reads
 * as "no section", and these are also what apply.ts writes as the Section
 * column's cell fills, so preview and applied column stay the same colors.
 */
export const SECTION_COLORS: readonly string[] = BG_PRESETS.filter(
  (color) => color !== "#e5e7eb",
);

export type CollectScriptResult =
  | { ok: true; rows: SectionsSuggestRow[] }
  | { ok: false; message: string };

/**
 * Every non-empty Original Script cell, in script order — what the analysis
 * reads. Refuses (with wording the panel shows verbatim) rather than silently
 * truncating: an analysis of half the script would LOOK complete.
 */
export function collectScriptRows(board: BoardDoc, sheetId: string): CollectScriptResult {
  const colId = board.sourceColumnId(sheetId);
  if (colId === undefined) {
    return { ok: false, message: "This board has no Original Script column to analyze." };
  }
  const rows: SectionsSuggestRow[] = [];
  let totalChars = 0;
  for (const rowId of board.sortedRowIds(sheetId)) {
    const text = board.getCellText(sheetId, rowId, colId);
    if (text.trim().length === 0) continue;
    const cut = text.length > SECTIONS_ROW_MAX_CHARS ? text.slice(0, SECTIONS_ROW_MAX_CHARS) : text;
    rows.push({ id: rowId, text: cut });
    totalChars += cut.length;
  }
  if (rows.length === 0) {
    return { ok: false, message: "The script is empty — write some rows first." };
  }
  if (rows.length > SECTIONS_MAX_ROWS) {
    return {
      ok: false,
      message: `The script has ${rows.length} rows — sections can analyze up to ${SECTIONS_MAX_ROWS} at once.`,
    };
  }
  if (totalChars > SECTIONS_MAX_CHARS_TOTAL) {
    return { ok: false, message: "The script is too long for one analysis." };
  }
  return { ok: true, rows };
}

/** Attach a stable preview/apply color to each section (cycling the palette). */
export function toPreviewSections(sections: readonly SuggestedSection[]): SectionPreview[] {
  return sections.map((section, index) => ({
    title: section.title,
    mood: section.mood,
    color: SECTION_COLORS[index % SECTION_COLORS.length] as string,
    rowIds: section.rowIds,
  }));
}

/**
 * The mood spans the preview and the apply read (F-139). A current server
 * sends them (`moods`); one built before F-139 sends only a mood per section,
 * which is turned into spans over the rows that were SENT (script order) so
 * the rest of the feature has one shape to read — a boundary there is where
 * the SECTION changed, which is all such an analysis can say. Rows with no
 * mood break a span, so the next moody row starts a new one.
 */
export function toPreviewMoods(
  response: SectionsSuggestResponse,
  rows: readonly SectionsSuggestRow[],
): MoodPreview[] {
  if (response.moods !== undefined) {
    return response.moods
      .filter((span) => span.rowIds.length > 0 && span.mood !== "")
      .map((span) => ({ mood: span.mood, rowIds: span.rowIds }));
  }
  const moodOf = new Map<string, string>();
  for (const section of response.sections) {
    for (const rowId of section.rowIds) moodOf.set(rowId, section.mood);
  }
  const spans: { mood: string; rowIds: string[] }[] = [];
  let open: { mood: string; rowIds: string[] } | null = null;
  for (const row of rows) {
    const mood = moodOf.get(row.id) ?? "";
    if (mood === "") {
      open = null;
      continue;
    }
    if (open !== null && open.mood === mood) {
      open.rowIds.push(row.id);
    } else {
      open = { mood, rowIds: [row.id] };
      spans.push(open);
    }
  }
  return spans;
}

/**
 * The slice of GridLayoutSnapshot the band layout needs — named separately so
 * this module (and its tests) never import from the grid.
 */
export interface SectionBandLayout {
  rowIndex: ReadonlyMap<string, number>;
  rowEdges: readonly number[];
  colEdges: readonly number[];
  originX: number;
  originY: number;
}

/** One contiguous run of a section, as a board-unit rect over the table. */
export interface SectionBand {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  title: string;
}

/**
 * The preview highlight geometry: each section's rows are resolved against the
 * CURRENT layout (a row deleted since the analysis simply has no band — C4),
 * then merged into contiguous runs so a section split across the script renders
 * as several same-colored bands, each labeled. Full table width: the section is
 * a property of the ROW, not of one column.
 */
export function sectionBands(
  sections: readonly SectionPreview[],
  layout: SectionBandLayout,
): SectionBand[] {
  const { rowIndex, rowEdges, colEdges, originX, originY } = layout;
  if (colEdges.length < 2 || rowEdges.length < 2) return [];
  const x = originX + (colEdges[0] as number);
  const w = (colEdges[colEdges.length - 1] as number) - (colEdges[0] as number);

  const bands: SectionBand[] = [];
  sections.forEach((section, sectionIndex) => {
    const indices = section.rowIds
      .map((rowId) => rowIndex.get(rowId))
      .filter((index): index is number => index !== undefined && index < rowEdges.length - 1)
      .sort((a, b) => a - b);
    let runStart = -1;
    let previous = -2;
    const flush = (end: number) => {
      if (runStart < 0) return;
      bands.push({
        key: `${sectionIndex}:${runStart}`,
        x,
        y: originY + (rowEdges[runStart] as number),
        w,
        h: (rowEdges[end + 1] as number) - (rowEdges[runStart] as number),
        color: section.color,
        title: section.title,
      });
    };
    for (const index of indices) {
      if (index !== previous + 1) {
        flush(previous);
        runStart = index;
      }
      previous = index;
    }
    flush(previous);
  });
  return bands;
}

/** One mood change, as a rule across the table at a row's top edge. */
export interface MoodMarker {
  key: string;
  x: number;
  /** The TOP edge of the first row of the span — the sentence it begins on. */
  y: number;
  w: number;
  mood: string;
}

/**
 * The mood-change geometry (F-139): one marker per span, on the top edge of
 * the span's first row that is still in the layout (a row deleted since the
 * analysis has no edge to mark — C4; a span with none left has no marker).
 * Full table width, like the bands: the mood is a property of the ROW.
 */
export function moodMarkers(
  moods: readonly MoodPreview[],
  layout: SectionBandLayout,
): MoodMarker[] {
  const { rowIndex, rowEdges, colEdges, originX, originY } = layout;
  if (colEdges.length < 2 || rowEdges.length < 2) return [];
  const x = originX + (colEdges[0] as number);
  const w = (colEdges[colEdges.length - 1] as number) - (colEdges[0] as number);

  const markers: MoodMarker[] = [];
  moods.forEach((span, spanIndex) => {
    const first = span.rowIds
      .map((rowId) => rowIndex.get(rowId))
      .filter((index): index is number => index !== undefined && index < rowEdges.length - 1)
      .reduce<number | undefined>((min, index) => (min === undefined || index < min ? index : min), undefined);
    if (first === undefined) return;
    markers.push({
      key: `${spanIndex}:${first}`,
      x,
      y: originY + (rowEdges[first] as number),
      w,
      mood: span.mood,
    });
  });
  return markers;
}

/** How much of the sentence a mood begins on the panel quotes. */
export const MOOD_EXCERPT_MAX_CHARS = 48;

/**
 * The opening of the Original Script line a mood span begins on — what the
 * panel quotes beside each mood, so "which sentence?" is answered in words
 * rather than row ids. Read from the LIVE board (the line may have been
 * edited since the analysis; a row deleted meanwhile yields "" — C4: its
 * cells outlive the row in the doc, so membership is checked, not the text),
 * single-line, cut with an ellipsis.
 */
export function moodExcerpt(board: BoardDoc, sheetId: string, rowId: string): string {
  const colId = board.sourceColumnId(sheetId);
  if (colId === undefined || !board.hasRow(sheetId, rowId)) return "";
  const text = board.getCellText(sheetId, rowId, colId).replace(/\s+/g, " ").trim();
  if (text.length <= MOOD_EXCERPT_MAX_CHARS) return text;
  return `${text.slice(0, MOOD_EXCERPT_MAX_CHARS).trimEnd()}…`;
}

/**
 * Error wording for the sections-suggest call — same contract as
 * `brainstormErrorMessage` (status + machine code in, one human sentence out),
 * sharing the upstream-failure vocabulary so the same provider problem reads
 * the same everywhere.
 */
export function sectionsErrorMessage(status: number, code: string, model?: string): string {
  if (code === "sections-not-configured") {
    return "AI is not configured on this server yet.";
  }
  if (code === "rate-limited" || code === "too-many-concurrent") {
    return "You've asked for a lot of AI work recently — give it a minute.";
  }
  if (code === "server-draining") {
    return "The server is restarting — try again in a few seconds.";
  }
  if (code === "unusable-response") {
    return "The model's answer wasn't usable. Try again, or pick a different model.";
  }
  if (code === "too-many-rows" || code === "too-many-chars") {
    return "The script is too long for one analysis.";
  }
  if (code === "invalid-model") {
    return "That model id doesn't look right — pick one from the list.";
  }
  const upstream = upstreamMessage(code, model);
  if (upstream !== null) return upstream;
  if (status === 403) return "You don't have permission to use AI on this board.";
  if (status === 404) return "This board no longer exists.";
  return "Couldn't analyze the script. Try again.";
}

/** The model the 502 body names, when it names one (see the route's catch). */
export function modelFromApiError(error: ApiError): string | undefined {
  const body = error.body as { model?: unknown } | undefined;
  return typeof body?.model === "string" ? body.model : undefined;
}
